import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Panel } from "@/components/common/Panel";
import { PERSONAS, ROLE_PERMISSIONS, ALL_PERMS } from "@/config/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { initials } from "@/lib/format";
import { Check, X } from "lucide-react";
import { PersistentSettings, ExistingMembers, UnavailableSettings } from "@/components/settings/PersistentSettings";
import { MfaPanel } from "@/components/settings/MfaPanel";
import OrgMembersPanel from "@/components/settings/OrgMembersPanel";
import PendingInvitations from "@/components/settings/PendingInvitations";
import { ConflictChecksPanel } from "@/components/dashboard/ConflictPanels";

const MENU = ["Profile", "Organisation", "Organisation Members", "My Invitations", "Team", "Roles & Permissions", "Intake Conflicts", "Practice Preferences", "Notifications", "Templates", "AI Preferences", "Security", "Billing Settings", "Appearance", "Developer / Demo"];

export default function Settings() {
  const { db, persona, personaRole, setPersona, resetDemo, seedDemo, clearChanges, scope, can, ccore, ccoreCan } = useApp();
  const navigate = useNavigate();
  const [active, setActive] = useState(() => { const s = new URLSearchParams(window.location.search).get("section"); return s && MENU.includes(s) ? s : "Profile"; });
  useEffect(() => { const s = new URLSearchParams(window.location.search).get("section"); if (s && MENU.includes(s)) setActive(s); }, [window.location.search]); // eslint-disable-line
  const menu = MENU.filter((m) => (m !== "Developer / Demo" || scope.demoControls) && (m !== "Intake Conflicts" || can("matter.create")) && (m !== "System Status" || can("admin.view")) && (m !== "Organisation Members" || (ccore?.ready && ccoreCan("org.member.read"))) && (m !== "My Invitations" || ccore?.ready));

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your workspace, team and preferences." />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Panel className="p-2 lg:col-span-1 h-fit">
          {menu.map((m) => <button key={m} onClick={() => setActive(m)} data-testid={`settings-menu-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={cn("block w-full rounded-md px-3 py-2 text-left text-sm", active === m ? "bg-copper-light text-copper" : "text-ink-2 hover:bg-surface")}>{m}</button>)}
        </Panel>

        <div className="lg:col-span-4">
          {active === "Intake Conflicts" && <ConflictChecksPanel />}
          {active === "Profile" && <ProfileSettings persona={persona} db={db} />}
          {active === "Organisation" && <PersistentSettings section="Organisation" />}
          {active === "Organisation Members" && <OrgMembersPanel />}
          {active === "My Invitations" && <PendingInvitations />}
          {active === "Team" && <ExistingMembers />}
          {active === "Roles & Permissions" && <RolesSettings />}
          {active === "Developer / Demo" && scope.demoControls && (
            <Panel className="p-5">
              <h3 className="font-display text-xl font-semibold text-ink">Developer / Demo Data</h3>
              <p className="mt-1 text-sm text-ink-2">Restore or clear the demo workspace. This layer exists only for the prototype.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <DangerAction label="Reset Demo Workspace" desc="Restores all matters, clients and records to the original demo set. Your local changes will be lost." onConfirm={() => resetDemo()} testid="reset-demo-btn" />
                <Button variant="outline" onClick={() => seedDemo()}>Seed Demo Data</Button>
                <DangerAction label="Clear Local Changes" desc="Removes locally stored changes and reloads the seed database." onConfirm={() => { clearChanges(); toast.success("Local changes cleared."); }} />
              </div>
            </Panel>
          )}
          {["Appearance", "Notifications", "Templates", "Practice Preferences", "Billing Settings"].includes(active) && <UnavailableSettings section={active} />}
          {active === "AI Preferences" && <UnavailableSettings section="AI Preferences" />}
          {active === "Security" && <SecuritySettings />}
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  const { listSessions, logout } = useApp();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [sessionError, setSessionError] = useState("");
  useEffect(() => { listSessions().then(setSessions).catch(() => setSessionError("Could not load active sessions. Reload this page to retry.")); }, []); // eslint-disable-line
  return (
    <Panel className="p-5" data-testid="security-settings"><h3 className="font-display text-xl font-semibold text-ink">Security</h3>
      <div className="mt-4 space-y-3">
        <Setting label="Session policy" desc="Access tokens expire after 30 minutes and are silently renewed from a rotating 14-day session. 5 failed sign-ins lock the account for 15 minutes." />
        <MfaPanel />
        <div>
          <div className="text-sm font-medium text-ink">Active sessions</div>
          <div className="mt-2 divide-y divide-line rounded-lg border border-line" data-testid="sessions-list">
            {sessionError && <p className="p-3 text-xs text-danger" data-testid="sessions-error">{sessionError}</p>}
            {sessions === null && !sessionError && <div className="p-3 text-xs text-ink-muted">Loading…</div>}
            {sessions?.map((s) => <div key={s.id} className="flex items-center justify-between p-3 text-xs" data-testid={`session-${s.id}`}><div><span className="font-medium text-ink">{s.current ? "This device" : (s.kind || "session")}</span> · {s.ip || "unknown IP"}<div className="truncate text-ink-muted max-w-md">{s.userAgent}</div></div><div className="flex items-center gap-2 text-ink-2"><span>Last used {String(s.lastUsedAt).slice(0, 16).replace("T", " ")}</span><Button variant="outline" size="sm" data-testid={`revoke-session-${s.id}`} onClick={async () => { if (!window.confirm(s.current ? "Sign out this device?" : "Revoke this device's session?")) return; try { await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sessions/${s.id}`); if (s.current) { await logout(); navigate("/login"); } else setSessions(await listSessions()); } catch { toast.error("Session could not be revoked. Retry."); } }}>Revoke</Button></div></div>)}
            {sessions?.length === 0 && <div className="p-3 text-xs text-ink-muted">No active sessions found.</div>}
          </div>
          <Button size="sm" variant="outline" className="mt-2" onClick={async () => { await logout({ everywhere: true }); toast.success("Signed out of all devices."); navigate("/login"); }} data-testid="logout-all-btn">Sign out of all devices</Button>
        </div>
      </div>
    </Panel>
  );
}

function ProfileSettings({ persona, db }) {
  const { setPersona, personaRole, scope, auth } = useApp();
  return (
    <div className="space-y-4">
      <PersistentSettings section="Profile" />

      {scope.demoControls && <Panel className="p-5" data-testid="persona-switcher">
        <h3 className="font-display text-xl font-semibold text-ink">Preview as role (demo only)</h3>
        <p className="mt-1 text-sm text-ink-2">Available only in preview environments. A preview persona can only <b>narrow</b> what you see — your server role ({scope.role}) remains authoritative and every action is still enforced by the backend.</p>
        <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3">
          {PERSONAS.filter((p) => p.role !== "ODR Neutral").map((p) => (
            <button key={p.role} onClick={() => { setPersona(p.role); toast.success(`Previewing as ${p.role}.`); }} className={cn("rounded-xl border p-3 text-left", personaRole === p.role ? "border-copper bg-copper-light" : "border-line hover:border-copper/40")} data-testid={`persona-${p.role.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="text-sm font-medium text-ink">{p.role}</div><div className="text-xs text-ink-2">{p.name}</div>
            </button>
          ))}
        </div>
      </Panel>}
    </div>
  );
}


function RolesSettings() {
  const roles = Object.keys(ROLE_PERMISSIONS);
  const cats = { Matters: ["matter.view", "matter.edit", "matter.create"], Documents: ["document.view", "document.upload", "document.delete"], Billing: ["billing.view", "billing.edit", "billing.margins.view"], Drafting: ["draft.view", "draft.edit", "draft.approve"], ODR: ["odr.view", "odr.caucus.view"], Admin: ["admin.view", "team.manage"] };
  const [role, setRole] = useState(roles[0]);
  return (
    <Panel className="p-5"><h3 className="font-display text-xl font-semibold text-ink">Roles & Permissions</h3>
      <div className="mt-3 flex flex-wrap gap-1.5">{roles.map((r) => <button key={r} onClick={() => setRole(r)} className={cn("rounded-full px-3 py-1 text-xs", role === r ? "bg-navy text-white" : "border border-line text-ink-2")}>{r}</button>)}</div>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">{Object.entries(cats).map(([cat, perms]) => (
        <div key={cat} className="rounded-xl border border-line p-3"><div className="mb-2 text-sm font-semibold text-ink">{cat}</div>
          {perms.map((p) => { const has = ROLE_PERMISSIONS[role].includes(p); return (
            <div key={p} className="flex items-center justify-between py-1 text-xs"><span className="text-ink-2">{p}</span>{has ? <Check className="h-4 w-4 text-success" /> : <X className="h-4 w-4 text-ink-muted" />}</div>
          ); })}
        </div>
      ))}</div>
    </Panel>
  );
}

const LabeledInput = ({ label, v }) => <div><div className="text-xs text-ink-2">{label}</div><Input className="mt-1" defaultValue={v} /></div>;
const Setting = ({ label, desc, children }) => <div className="flex items-center justify-between rounded-lg border border-line px-4 py-3"><div><div className="text-sm font-medium text-ink">{label}</div>{desc && <div className="text-xs text-ink-2">{desc}</div>}</div>{children}</div>;

function DangerAction({ label, desc, onConfirm, testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><Button variant="outline" className="border-danger/40 text-danger hover:bg-danger-bg" data-testid={testid}>{label}</Button></AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{label}?</AlertDialogTitle><AlertDialogDescription>{desc}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onConfirm}>Confirm</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
